/**
 * Payment receipt email renderer.
 *
 * Sent when a payment is recorded against an invoice (Piece 3 of the
 * invoicing Tier-1 arc). A receipt is intentionally shorter than an
 * invoice email — no line-item breakdown, no due-date, no "please pay"
 * tone. The payment landed; this acknowledges it.
 *
 * Mirrors invoiceEmail.ts's chrome (same inline-style HTML, same branding
 * inputs, same esc/formatCurrency/formatDate helpers) so receipts look
 * like a sibling of the invoice in the recipient's inbox.
 *
 * Subject varies by remaining balance:
 *   - "Paid in full — Invoice <n>"     (remainingBalance === 0)
 *   - "Payment received — Invoice <n>" (remainingBalance > 0)
 *
 * Two callouts depending on balance:
 *   - "This invoice is now paid in full. Thank you!"  (positive green)
 *   - "Remaining balance: $X.XX of $Y total"          (neutral, brand accent)
 *
 * No DELETE-side counterpart: removing a payment does NOT send any email
 * (no "we un-received your payment" notification — that's a record-correction
 * action the operator takes, not customer-facing).
 */

export type ReceiptPaymentMethod = 'cash' | 'check' | 'card' | 'ach' | 'other'

const METHOD_LABELS: Record<ReceiptPaymentMethod, string> = {
  cash: 'Cash', check: 'Check', card: 'Card', ach: 'ACH', other: 'Other',
}

export interface ReceiptEmailData {
  /** Invoice number for the subject and details block. */
  invoiceNumber: string
  /** Recipient's display name, used in the greeting. */
  contactName: string
  /** Workspace name, used as the sender's friendly identity. */
  workspaceName: string
  /** Currency code (e.g., 'USD'). */
  currency: string
  /** Invoice total — shown in the balance callout alongside remaining. */
  invoiceTotal: number
  /** This payment's amount (the headline number). */
  paymentAmount: number
  /** This payment's method. */
  paymentMethod: ReceiptPaymentMethod
  /** When the payment was received (ISO string). */
  paymentDate: string
  /** Optional reference (check #, transaction id…). */
  paymentReference?: string | null
  /** Authoritative remaining balance AFTER this payment (from the post-reconcile invoice.amount_due). */
  remainingBalance: number
  /** Workspace brand color (hex, e.g. '#ff6b00'). Falls back to platform purple #7c3aed when null/undefined. */
  brandColor?: string | null
  /** Workspace logo URL (public Supabase Storage URL). Renders as <img> in the header right slot when present. */
  logoUrl?: string | null
}

export interface ReceiptEmailRender {
  subject: string
  html: string
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount || 0)
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  } catch {
    return iso
  }
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function renderReceiptEmail(data: ReceiptEmailData): ReceiptEmailRender {
  const paidInFull = (data.remainingBalance || 0) <= 0
  const subject = paidInFull
    ? `Paid in full — Invoice ${data.invoiceNumber}`
    : `Payment received — Invoice ${data.invoiceNumber}`

  const brand = data.brandColor || '#7c3aed'
  const greeting = `Hi ${esc(data.contactName.split(' ')[0] || data.contactName)},`

  // Balance callout — two visual variants. Paid-in-full uses positive green
  // (matches the app's --positive token semantic); partial uses brand accent.
  const balanceCallout = paidInFull
    ? `
      <div style="margin-top:24px; padding:16px 20px; background:#e8f7f0; border:1px solid #c5ead7; border-radius:10px;">
        <div style="font-size:14px; font-weight:700; color:#0a8055; margin-bottom:2px;">
          This invoice is now paid in full.
        </div>
        <div style="font-size:13px; color:#0a8055;">Thank you!</div>
      </div>
    `
    : `
      <div style="margin-top:24px; padding:16px 20px; background:#f7f7f9; border:1px solid #eee; border-radius:10px;">
        <div style="font-size:11px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px;">Remaining balance</div>
        <div style="font-size:20px; font-weight:800; color:${brand};">${formatCurrency(data.remainingBalance, data.currency)}</div>
        <div style="font-size:12px; color:#666; margin-top:2px;">of ${formatCurrency(data.invoiceTotal, data.currency)} total</div>
      </div>
    `

  // Reference row in the details table is optional.
  const referenceRow = data.paymentReference && data.paymentReference.trim()
    ? `
      <tr>
        <td style="padding:8px 0; color:#666; font-size:13px;">Reference</td>
        <td style="padding:8px 0; text-align:right; font-size:13px; color:#111; font-family:'SF Mono','Menlo',monospace;">${esc(data.paymentReference)}</td>
      </tr>
    `
    : ''

  // Email body. Inline styles only — many email clients strip <style> blocks.
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${esc(subject)}</title>
</head>
<body style="margin:0; padding:0; background:#f5f5f7; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <div style="max-width:600px; margin:0 auto; padding:24px 16px;">

    <div style="background:#fff; border-radius:12px; padding:32px; box-shadow:0 1px 3px rgba(0,0,0,0.06);">

      <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; padding-bottom:20px; border-bottom:1px solid #eee;">
        <div>
          <div style="font-size:12px; font-weight:700; color:${brand}; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px;">Payment received</div>
          <div style="font-size:22px; font-weight:800; color:#111;">Invoice ${esc(data.invoiceNumber)}</div>
        </div>
        ${data.logoUrl ? `<img src="${esc(data.logoUrl)}" alt="" style="max-height:40px; max-width:120px;" />` : ''}
      </div>

      <p style="margin:0 0 8px; font-size:15px; color:#333;">${greeting}</p>
      <p style="margin:0 0 24px; font-size:15px; color:#333;">Thank you — we've received your payment.</p>

      <div style="padding:20px 24px; background:#f7f7f9; border-radius:10px; text-align:center;">
        <div style="font-size:11px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:6px;">Amount paid</div>
        <div style="font-size:32px; font-weight:800; color:#111; letter-spacing:-0.02em;">${formatCurrency(data.paymentAmount, data.currency)}</div>
      </div>

      <table cellpadding="0" cellspacing="0" style="width:100%; margin-top:20px; border-collapse:collapse;">
        <tr>
          <td style="padding:8px 0; color:#666; font-size:13px;">Method</td>
          <td style="padding:8px 0; text-align:right; font-size:13px; color:#111; font-weight:600;">${METHOD_LABELS[data.paymentMethod]}</td>
        </tr>
        <tr>
          <td style="padding:8px 0; color:#666; font-size:13px; border-top:1px solid #eee;">Date</td>
          <td style="padding:8px 0; text-align:right; font-size:13px; color:#111; border-top:1px solid #eee;">${formatDate(data.paymentDate)}</td>
        </tr>
        ${referenceRow ? referenceRow.replace('padding:8px 0; color:#666', 'padding:8px 0; color:#666; border-top:1px solid #eee').replace('padding:8px 0; text-align:right', 'padding:8px 0; text-align:right; border-top:1px solid #eee') : ''}
      </table>

      ${balanceCallout}

      <p style="margin:32px 0 0; font-size:13px; color:#666; padding-top:20px; border-top:1px solid #eee;">
        Questions about this payment? Just reply to this email.
      </p>

    </div>

    <p style="margin:16px 0 0; text-align:center; font-size:12px; color:#999;">
      Sent by ${esc(data.workspaceName)} via Orkestra
    </p>

  </div>
</body>
</html>
  `.trim()

  return { subject, html }
}
